export const SYMPTOM_OPTIONS = [
  { id: "feelingHopeless", label: "Often Feel Hopeless" },
  {
    id: "persistentWorryOrRestlessness",
    label: "Persistent Worry or Restlessness",
  },
  {
    id: "troubleFocusingOrConcentrating",
    label: "Trouble Focusing or Concentrating",
  },
  {
    id: "fatigueOrLowEnergy",
    label: "Fatigue or Low Energy",
  },
  {
    id: "sleepDisturbances",
    label: "Sleep Disturbances",
  },
  {
    id: "moodChanges",
    label: "Mood Changes",
  },
  {
    id: "changesInAppetiteOrWeight",
    label: "Changes in Appetite or Weight",
  },
  {
    id: "impulsivityOrRiskTaking",
    label: "Impulsivity or Risk-Taking",
  },
  {
    id: "poorTimeManagementOrOrganization",
    label: "Poor Time Management or Organization",
  },
  {
    id: "physicalSymptomsWithoutClearCause",
    label: "Physical Symptoms Without Clear Cause",
  },
  {
    id: "socialWithdrawalOrAvoidance",
    label: "Social Withdrawal or Avoidance",
  },
] as const;

export type SymptomId = (typeof SYMPTOM_OPTIONS)[number]["id"];

export const OUTCOME_OPTIONS = [
  {
    id: "reducedWorryAndRestlessness",
    label: "Reduced Worry and Restlessness",
  },
  {
    id: "improvedFocusAndClarity",
    label: "Improved Focus and Mental Clarity",
  },
  {
    id: "moreEnergyAndMotivation",
    label: "More Energy and Motivation",
  },
  {
    id: "betterSleep",
    label: "Better, More Restful Sleep",
  },
  {
    id: "stableMood",
    label: "More Stable Mood",
  },
  {
    id: "healthyAppetiteAndWeight",
    label: "Healthier Appetite and Weight",
  },
  {
    id: "betterImpulseControl",
    label: "Better Impulse Control",
  },
  {
    id: "improvedOrganization",
    label: "Improved Organization and Time Management",
  },
  {
    id: "fewerPhysicalStressSymptoms",
    label: "Fewer Physical Stress Symptoms",
  },
  {
    id: "strongerSocialConnection",
    label: "Stronger Social Connection and Engagement",
  },
] as const;

export type OutcomeId = (typeof OUTCOME_OPTIONS)[number]["id"];

export const FAMILY_HISTORY_CONDITIONS = [
  { id: "hypertension", label: "Hypertension" },
  { id: "stroke", label: "Stroke" },
  { id: "epilepsy", label: "Epilepsy" },
  { id: "diabetes", label: "Diabetes" },
  { id: "bipolarDisorder", label: "Bipolar Disorder" },
  { id: "schizophrenia", label: "Schizophrenia" },
] as const;

export const FAMILY_HISTORY_RELATIONS = [
  { id: "mother", label: "Mother" },
  { id: "father", label: "Father" },
  { id: "maternalGrandparent", label: "Maternal Grandparent" },
  { id: "paternalGrandparent", label: "Paternal Grandparent" },
] as const;

export type FamilyHistoryConditionId =
  (typeof FAMILY_HISTORY_CONDITIONS)[number]["id"];
export type FamilyHistoryRelationId =
  (typeof FAMILY_HISTORY_RELATIONS)[number]["id"];

export const ALLERGY_OPTIONS = [
  {
    id: "allergicToCertainMedications",
    label: "Allergic to Certain Medications",
  },
  { id: "milkDairy", label: "Milk / Dairy" },
  { id: "penicillin", label: "Penicillin" },
  { id: "shellfish", label: "Shellfish" },
  { id: "treeNuts", label: "Tree Nuts" },
  { id: "peanuts", label: "Peanuts" },
  { id: "sesame", label: "Sesame" },
  { id: "latex", label: "Latex" },
  { id: "wheat", label: "Wheat" },
  { id: "eggs", label: "Eggs" },
  { id: "fish", label: "Fish" },
  { id: "soy", label: "Soy" },
  { id: "none", label: "None" },
] as const;

export type AllergyId = (typeof ALLERGY_OPTIONS)[number]["id"];

export const MEDICAL_CONDITION_OPTIONS = [
  {
    id: "sleepDisorders",
    label: "Sleep Disorders (Insomnia or Sleep Apnea)",
  },
  {
    id: "eatingDisorders",
    label: "Eating Disorders or Disordered Eating",
  },
  {
    id: "gastrointestinalIssues",
    label: "Gastrointestinal Issues (IBS or GERD)",
  },
  {
    id: "cardiacImplants",
    label: "Cardiac Implants (Pacemaker etc.)",
  },
  {
    id: "migrainesOrChronicHeadaches",
    label: "Migraines or Chronic Headaches",
  },
  {
    id: "discolorationOfExtremities",
    label: "Discoloration of Extremities",
  },
  {
    id: "autoimmuneConditions",
    label: "Autoimmune Conditions",
  },
  {
    id: "highBloodPressure",
    label: "High Blood Pressure",
  },
  {
    id: "hormonalImbalances",
    label: "Hormonal Imbalances",
  },
  {
    id: "shortnessOfBreath",
    label: "Shortness of Breath",
  },
  {
    id: "lowBloodPressure",
    label: "Low Blood Pressure",
  },
  {
    id: "historyOfAsthma",
    label: "History of Asthma",
  },
  {
    id: "thyroidDisorders",
    label: "Thyroid Disorders",
  },
  {
    id: "yellowingOfEyes",
    label: "Yellowing of Eyes",
  },
  {
    id: "historyOfCovid",
    label: "History of COVID",
  },
  {
    id: "heartDisease",
    label: "Heart Disease",
  },
  {
    id: "chronicPain",
    label: "Chronic Pain",
  },
  {
    id: "heartAttack",
    label: "Heart Attack",
  },
  {
    id: "diabetes",
    label: "Diabetes",
  },
  {
    id: "dialysis",
    label: "Dialysis",
  },
  {
    id: "epilepsy",
    label: "Epilepsy",
  },
  {
    id: "cancer",
    label: "Cancer",
  },
  {
    id: "stroke",
    label: "Stroke",
  },
  {
    id: "none",
    label: "None",
  },
] as const;

export type MedicalConditionId =
  (typeof MEDICAL_CONDITION_OPTIONS)[number]["id"];

export const DIET_OPTIONS = [
  { id: "mediterranean", label: "Mediterranean" },
  { id: "ketoLowCarb", label: "Keto / Low-Carb" },
  { id: "vegetarian", label: "Vegetarian" },
  { id: "vegan", label: "Vegan" },
  { id: "pescatarian", label: "Pescatarian" },
  { id: "carnivore", label: "Carnivore" },
  { id: "paleo", label: "Paleo" },
  { id: "lowCalorie", label: "Low-Calorie / Calorie Counting" },
  { id: "glutenFree", label: "Gluten-Free" },
  { id: "intermittentFasting", label: "Intermittent Fasting" },
  { id: "noSpecificDiet", label: "No Specific Diet" },
] as const;

export type DietId = (typeof DIET_OPTIONS)[number]["id"];

export const EXERCISE_OPTIONS = [
  { id: "none", label: "No Regular Exercise" },
  { id: "under30MinWeek", label: "Under 30 Minutes / Week" },
  { id: "30To60MinWeek", label: "30–60 Minutes / Week" },
  { id: "1To2HoursWeek", label: "1–2 Hours / Week" },
  { id: "2To3HoursWeek", label: "2–3 Hours / Week" },
  { id: "3To5HoursWeek", label: "3–5 Hours / Week" },
  { id: "5PlusHoursWeek", label: "5+ Hours / Week" },
] as const;

export type ExerciseId = (typeof EXERCISE_OPTIONS)[number]["id"];

export const SLEEP_HABIT_OPTIONS = [
  { id: "underSixHours", label: "Under 6 Hours Most Nights" },
  { id: "sevenToNineHours", label: "7–9 Hours Most Nights" },
  { id: "overNineHours", label: "Over 9 Hours Most Nights" },
  { id: "irregularSchedule", label: "Irregular Sleep Schedule" },
  { id: "difficultyFallingAsleep", label: "Difficulty Falling Asleep" },
  { id: "frequentNightWaking", label: "Frequent Night Waking" },
  { id: "earlyMorningWaking", label: "Early Morning Waking" },
  { id: "usesSleepAids", label: "Uses Sleep Aids" },
  { id: "shiftWorkSchedule", label: "Shift Work / Night Schedule" },
] as const;

export type SleepHabitId = (typeof SLEEP_HABIT_OPTIONS)[number]["id"];

export const LIFESTYLE_FACTOR_OPTIONS = [
  { id: "cigaretteSmoker", label: "Cigarette Smoker" },
  { id: "nicotineUser", label: "Nicotine User" },
  { id: "alcoholConsumption", label: "Alcohol Consumption" },
  { id: "fastFoodConsumption", label: "Fast Food Consumption" },
  { id: "cannabisUser", label: "Cannabis User" },
  { id: "kratomUser", label: "Kratom User" },
  { id: "recreationalSubstanceUser", label: "Recreational Substance User" },
  { id: "none", label: "None" },
] as const;

export type LifestyleFactorId = (typeof LIFESTYLE_FACTOR_OPTIONS)[number]["id"];

export const SCREENING_PREGNANCY_OPTIONS = [
  { id: "pregnant", label: "Pregnant" },
  { id: "couldBePregnant", label: "Could Be Pregnant" },
  { id: "breastfeeding", label: "Breastfeeding" },
  { id: "none", label: "None" },
] as const;

export const SCREENING_PROGRAM_OPTIONS = [
  { id: "interestedInRetreats", label: "Interested in Retreats" },
  { id: "wantsSupervision", label: "Want Supervision for Consumption" },
] as const;

export const SCREENING_OPTIONS = [
  ...SCREENING_PREGNANCY_OPTIONS,
  ...SCREENING_PROGRAM_OPTIONS,
] as const;

export type ScreeningId = (typeof SCREENING_OPTIONS)[number]["id"];
